import { useState } from 'react';
import type { RecipeDetail } from '@mlc/shared';
import { setRecipesVisibility } from './api';
import { Banner, Button } from './ui';

export interface VisibilityToggleProps {
  detail: RecipeDetail;
  /** Recharge la fiche : la visibilité change aussi l'état des liens de partage. */
  onChanged: () => void;
}

/**
 * Bascule privé ↔ partagé (US-49, vague E).
 *
 * **C'est ici que le mot « privé » apparaît enfin à l'écran.** Il n'y était
 * pas avant, et c'est ce qui rendait l'ordre de la vague E tenable : tant que
 * l'application ne promet rien, elle ne peut pas mentir. Les vingt et un
 * vecteurs sont fermés (US-45 à US-48), la promesse peut être faite.
 *
 * ### La seule vraie difficulté d'ergonomie de la story
 *
 * **Le mot « partager » a disparu de l'interface** (US-56). Deux gestes
 * différents le portaient — rendre une recette visible de la famille, et
 * envoyer un lien public à quelqu'un d'extérieur —, et trois vocabulaires
 * coexistaient pour le premier. Le premier geste est devenu une
 * **appartenance** (« dans le livre de famille » / « gardée pour moi »), le
 * second garde « Envoyer un lien ». Un mot qui ne désigne plus rien ne peut
 * plus désigner deux choses.
 *
 * Pour mémoire, l'état d'origine :
 *
 * - **la visibilité**, qui décide qui, *dans la famille*, voit la recette ;
 * - **le lien public** (US-33), qui l'envoie à quelqu'un qui n'a pas de compte.
 *
 * Les confondre dans un même bouton serait la faute la plus facile à commettre
 * ici. Ils sont donc dans deux blocs distincts, et **ce contrôle ne dit jamais
 * « partager » seul** : il dit « avec la famille ». Envoyer un lien ne change
 * pas la visibilité ; changer la visibilité vers `private` révoque les liens,
 * et le dit avant de le faire.
 */
export function VisibilityToggle({ detail, onChanged }: VisibilityToggleProps): React.JSX.Element {
  const [confirmer, setConfirmer] = useState(false);
  const [erreur, setErreur] = useState<string | null>(null);
  const [enCours, setEnCours] = useState(false);
  const partagee = detail.visibility === 'shared';

  function basculer(vers: 'private' | 'shared'): void {
    setEnCours(true);
    setErreur(null);
    setRecipesVisibility([detail.id], vers)
      .then(() => { setConfirmer(false); onChanged(); })
      .catch((e: unknown) => { setErreur(e instanceof Error ? e.message : String(e)); })
      .finally(() => { setEnCours(false); });
  }

  return (
    <section
      aria-labelledby={`visibilite-${detail.id}`}
      // **Une ligne, pas un panneau** (refonte éditoriale). Ce bloc ouvrait la
      // fiche : un cadre haut de 220 px portant un titre en capitales, une
      // phrase, un bouton étiré sur toute la largeur disponible — 1740 px sur
      // un écran de bureau — et une mention d'auteur. Le premier objet d'une
      // page dont le sujet est un plat était donc un réglage de
      // confidentialité, et l'action la plus large de l'écran, « la mettre
      // dans le livre de famille », dépassait « Cuisiner » d'un facteur huit.
      //
      // Le contenu ne change pas : l'état, le geste pour en changer, la
      // provenance. Il tient sur une ligne, se replie sur deux au format
      // téléphone, et le bouton reprend la largeur de son libellé. Le
      // dépliage de confirmation, lui, garde sa forme : c'est le seul moment
      // où cette décision mérite toute la place.
      className="flex flex-col gap-3 rounded-card border border-border bg-surface p-4"
    >
      <h2 id={`visibilite-${detail.id}`} className="sr-only">Le livre de famille</h2>

      {erreur !== null && <Banner tone="danger">{erreur}</Banner>}

      {confirmer ? (
        <div className="flex flex-col gap-2">
          <Banner tone="info">
            {partagee
              ? 'Elle redeviendra invisible pour les autres, et les liens déjà envoyés cesseront de fonctionner.'
              : 'La recette rejoindra le livre de famille. Vos notes personnelles, votre journal de réalisations et votre rangement restent à vous : c’est la recette qui rejoint le livre, pas votre carnet.'}
          </Banner>
          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              variant="primary"
              disabled={enCours}
              onClick={() => { basculer(partagee ? 'private' : 'shared'); }}
            >
              {partagee ? 'La garder pour moi' : 'La mettre dans le livre de famille'}
            </Button>
            <Button type="button" variant="ghost" onClick={() => { setConfirmer(false); }}>Annuler</Button>
          </div>
        </div>
      ) : (
        <div className="flex flex-wrap items-center justify-between gap-3">
          <p className="flex items-center gap-2 text-sm text-foreground-secondary">
            {/* Une pastille plutôt qu'une icône : l'état se lit d'un coup
                d'œil, et le texte juste à côté le dit en toutes lettres pour
                qui ne distingue pas les couleurs. */}
            <span
              aria-hidden="true"
              className={`size-2 shrink-0 rounded-pill ${partagee ? 'bg-accent' : 'bg-border-strong'}`}
            />
            {partagee
              ? 'Dans le livre de famille : tous les membres la voient.'
              : 'Gardée pour vous. Personne d’autre ne sait qu’elle existe.'}
          </p>
          <Button
            type="button"
            variant={partagee ? 'quiet' : 'secondary'}
            size="compact"
            disabled={enCours}
            onClick={() => { setConfirmer(true); }}
          >
            {partagee ? 'La garder pour moi' : 'La mettre dans le livre'}
          </Button>
        </div>
      )}

      {detail.owner_name !== null && (
        <p className="text-meta">
          Ajoutée par {detail.owner_name}
          {/* Comparaison sur les identifiants, pas sur les noms affichés
              (US-52) : deux membres prénommés Claire se seraient sinon effacé
              cette mention l'un l'autre. */}
          {detail.updated_by_name !== null && detail.updated_by !== detail.owner_id
            && <> · modifiée par {detail.updated_by_name}</>}
        </p>
      )}
    </section>
  );
}
